/**
 * ItemTab — tab "Item" của Production Report (`/report/batch-report/item`).
 *
 * READ-ONLY. Block "Quality Standard": item của PO (issue ∪ receive) + Standard
 * MC/Blacks/Brokens/FM lấy từ `tlc_factory.view_factory_deduction_standard_detail`
 * (factory-level, KHÔNG phải po.standard_edit) + quality_premium/price_from_g2/fx
 * (CTE premium) + description (list_and_valo). Nguồn: query mẫu
 * `batchreport_quality.txt` → `GET /factories/{fc}/batch-report/{po}/item-info`.
 */
import { Alert, Box, CircularProgress, Stack, Typography } from '@mui/material'
import { forwardRef, useEffect, useImperativeHandle, useMemo } from 'react'

import { useGetQualityStandardListQuery, type FactoryCell } from '@/store/api/factoryApi'
import { useGetBatchReportItemInfoQuery, type PoItemInfoRow } from '@/store/api/factoryBomApi'

import { FilterTable, type FilterCol } from './FilterTable'
import { BlockTitle } from './reportUi'
import { COLORS } from './styleTokens'

interface Props {
  factoryCode: string
  poNo: string
  /** Giữ để parent tương thích — read-only nên không còn ý nghĩa edit. */
  canEdit?: boolean
  poOptions?: string[]
  poMeta?: Record<string, { bomCode: string; poDate: string }>
  onDirtyChange?: (dirty: boolean) => void
}

export interface ItemTabHandle {
  /** Read-only → luôn true (không có gì để lưu). Parent confirm dialog vẫn gọi. */
  saveAll: () => Promise<boolean>
}

/** Standard MC/Blacks/Brokens/FM là %. Render: "12.50%". */
const fmtPct = (v: number | null): string =>
  v == null ? '' : `${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`

/** Số thường (premium/price/fx) 2 chữ số thập phân. */
const fmtNum2 = (v: number | null): string =>
  v == null ? '' : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const numCell = (v: number | null, fmt: (n: number | null) => string) => (
  <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(v)}</Box>
)

const ItemTab = forwardRef<ItemTabHandle, Props>(function ItemTab(
  { factoryCode, poNo, onDirtyChange },
  ref,
) {
  const skip = !factoryCode || !poNo.trim()
  const { data, isFetching, isError } = useGetBatchReportItemInfoQuery(
    { factoryCode, poNo: poNo.trim() },
    { skip, refetchOnMountOrArgChange: true },
  )
  const rows = useMemo(() => data?.items ?? [], [data])

  // Read-only: không có dirty, saveAll no-op.
  useImperativeHandle(ref, () => ({ saveAll: async () => true }), [])
  useEffect(() => {
    onDirtyChange?.(false)
  }, [onDirtyChange])

  const stdCol = (key: 'mc' | 'blacks' | 'brokens' | 'brown' | 'fm', label: string): FilterCol<PoItemInfoRow> => ({
    key,
    label,
    width: 140,
    align: 'right',
    numeric: true,
    value: (r) => r[key],
    render: (r) => numCell(r[key], fmtPct),
  })
  const numCol = (
    key: 'qualityPremium' | 'priceVsG2' | 'fx',
    label: string,
  ): FilterCol<PoItemInfoRow> => ({
    key,
    label,
    width: 150,
    align: 'right',
    numeric: true,
    value: (r) => r[key],
    render: (r) => numCell(r[key], fmtNum2),
  })

  const cols: FilterCol<PoItemInfoRow>[] = [
    { key: 'item', label: 'Item', width: 170, frozen: true, value: (r) => r.item },
    { key: 'phase', label: 'Phase', width: 150, filter: 'checkboxes', value: (r) => r.phase },
    stdCol('mc', 'Standard MC'),
    stdCol('blacks', 'Standard Blacks'),
    stdCol('brokens', 'Standard Brokens'),
    stdCol('brown', 'Standard Brown'),
    stdCol('fm', 'Standard FM'),
    numCol('qualityPremium', 'Quality Premium'),
    numCol('priceVsG2', 'Price vs G2 (USD)'),
    numCol('fx', 'FX'),
    { key: 'description', label: 'Description', width: 220, value: (r) => r.description },
  ]

  return (
    <Box sx={{ p: 1 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ gap: 1, flexWrap: 'wrap' }}>
        <BlockTitle>Quality Standard</BlockTitle>
      </Stack>

      {isError ? (
        <Alert severity="error" sx={{ mt: 1 }}>
          Could not load items for this PO.
        </Alert>
      ) : null}

      {skip ? (
        <EmptyState message="Select a PO to view items." />
      ) : isFetching && rows.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={22} />
        </Box>
      ) : rows.length === 0 && !isError ? (
        <EmptyState message="No items for this PO." />
      ) : rows.length > 0 ? (
        <Box sx={{ mt: 0.5 }}>
          <FilterTable
            storageKey="batchreport.item"
            columns={cols}
            rows={rows}
            rowKey={(r, i) => `${r.item ?? ''}-${i}`}
            searchPlaceholder="Search items…"
          />
        </Box>
      ) : null}

      {/* Block phụ: chỉ cột Rejects/Byproduct của bảng Quality Standard. */}
      <RejectedByproductStandard factoryCode={factoryCode} />
    </Box>
  )
})

export default ItemTab

/* ---------- Block "Rejects/Byproduct Standard" ------------------------------
 * Copy bảng /baoloc/quality-standard nhưng CHỈ giữ cột Rejects/Byproduct
 * (quality_factory = 'rejected_byproduct') — bỏ mọi quality khác. Read-only,
 * factory-level (không phụ thuộc PO). Dùng FilterTable chung (/table-style). */

type PivotRow = Record<string, FactoryCell>
const cellStr = (v: FactoryCell): string => (v == null ? '' : String(v))
const cellNum = (v: FactoryCell): number | null => {
  if (v == null || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}
const fmtNum4 = (v: FactoryCell): string => {
  const n = cellNum(v)
  return n == null ? '' : n.toLocaleString('en-US', { maximumFractionDigits: 4 })
}

const RBP_COLS: FilterCol<PivotRow>[] = [
  // Freeze cột định danh = Field Name (sticky left → phải là cột đầu).
  { key: 'field_name', label: 'Field Name', width: 240, frozen: true, value: (r) => cellStr(r.field_name) },
  { key: 'parameter_code', label: 'Parameter', width: 160, value: (r) => cellStr(r.parameter_code) },
  {
    key: 'rejected_byproduct',
    label: 'Rejects/Byproduct',
    width: 180,
    align: 'right',
    numeric: true,
    value: (r) => cellNum(r.rejected_byproduct),
    render: (r) => <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtNum4(r.rejected_byproduct)}</Box>,
  },
]

function RejectedByproductStandard({ factoryCode }: { factoryCode: string }) {
  const { data, isFetching, isError } = useGetQualityStandardListQuery(
    { factoryCode },
    { skip: !factoryCode },
  )
  const rows = useMemo(() => (data?.items ?? []) as PivotRow[], [data])

  return (
    <Box sx={{ mt: 2.5 }}>
      <BlockTitle>Rejects/Byproduct Standard</BlockTitle>
      <Typography
        sx={{ mt: 0.25, mb: 0.25, fontSize: '0.72rem', color: COLORS.textMuted, fontStyle: 'italic' }}
      >
        Read-only here. To edit Rejects/Byproduct values, go to the Quality Standard page.
      </Typography>
      {isError ? (
        <Alert severity="error" sx={{ mt: 1 }}>
          Could not load deduction standards.
        </Alert>
      ) : isFetching && rows.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={22} />
        </Box>
      ) : rows.length === 0 ? (
        <EmptyState message="No deduction standards for this factory." />
      ) : (
        <Box sx={{ mt: 0.5 }}>
          <FilterTable
            storageKey="batchreport.rbpdeduction"
            columns={RBP_COLS}
            rows={rows}
            rowKey={(r, i) => cellStr(r.parameter_code) || i}
            searchPlaceholder="Search parameters…"
          />
        </Box>
      )}
    </Box>
  )
}

function EmptyState({ message }: { message: string }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 200,
        border: `1px dashed ${COLORS.border}`,
        borderRadius: 1,
        mt: 1,
      }}
    >
      <Typography sx={{ fontSize: '0.85rem', color: COLORS.textSubtle }}>{message}</Typography>
    </Box>
  )
}
